/**
 * JKL Slideshow Component
 * Migrated from: shopify-headless/app/components/sanity/heroes/Home.tsx
 *
 * Features:
 * - Swiper-based slider with autoplay
 * - Navigation arrows and pagination dots
 * - Pause on hover
 * - Video support with auto-advance
 * - Half-width slide support (grouped on desktop, individual on mobile)
 * - Theme Editor integration
 *
 * Half-width slides approach:
 * - Desktop: Shows grouped slides (2 half-width in one swiper-slide)
 * - Mobile: Shows individual slides
 * - We physically remove hidden slides from Swiper before init
 */

import Swiper from './swiper-bundle.esm.browser.min.js';

class JklSlideshow extends HTMLElement {
  constructor() {
    super();

    // Instance properties
    this.swiper = null;
    this.sectionId = this.dataset.sectionId;
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5;
    this.showArrows = this.dataset.showArrows === 'true';
    this.showDots = this.dataset.showDots === 'true';
    this.slidesCount = parseInt(this.dataset.slidesCount, 10) || 0;
    this.desktopSlidesCount = parseInt(this.dataset.desktopSlidesCount, 10) || 0;
    this.mobileSlidesCount = parseInt(this.dataset.mobileSlidesCount, 10) || 0;

    // Track current breakpoint (<1024: half-width slide DOM; <=767: mobile layout / variable slide height)
    this.isMobile = window.innerWidth < 1024;
    this.isCompactMobile = window.innerWidth <= 767;

    // Store original HTML for rebuilding on breakpoint change
    this.originalSwiperHTML = null;

    // DOM elements
    this.swiperContainer = null;
    this.swiperWrapper = null;
    this.prevButton = null;
    this.nextButton = null;
    this.paginationEl = null;

    // Debounce timer for resize
    this.resizeTimer = null;

    // Bind methods
    this.initSwiper = this.initSwiper.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Use requestAnimationFrame to ensure CSS is loaded
      requestAnimationFrame(() => this.init());
    }
  }

  init() {
    // Get DOM elements
    this.swiperContainer = this.querySelector('.jkl-slideshow__swiper');
    this.swiperWrapper = this.querySelector('.swiper-wrapper');
    this.prevButton = this.querySelector('.jkl-slideshow__nav-prev');
    this.nextButton = this.querySelector('.jkl-slideshow__nav-next');
    this.paginationEl = this.querySelector('.jkl-slideshow__pagination');

    if (!this.swiperContainer || !this.swiperWrapper) return;

    // Store original HTML on first init (for rebuilding on resize)
    if (!this.originalSwiperHTML) {
      this.originalSwiperHTML = this.swiperWrapper.innerHTML;
    }

    // Update breakpoint state
    this.isMobile = window.innerWidth < 1024;
    this.isCompactMobile = window.innerWidth <= 767;

    // Only initialize Swiper if we have slides
    if (this.slidesCount > 0) {
      this.initSwiper();
    }

    // Listen for Theme Editor events
    document.addEventListener('shopify:section:load', this.handleSectionLoad);

    // Listen for resize to handle breakpoint changes
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Handle window resize - reinitialize Swiper if breakpoint changes
   */
  handleResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      const wasMobile = this.isMobile;
      const wasCompactMobile = this.isCompactMobile;
      this.isMobile = window.innerWidth < 1024;
      this.isCompactMobile = window.innerWidth <= 767;

      // Rebuild when half-width grouping changes or when compact/mobile image layout toggles (autoHeight)
      if (wasMobile !== this.isMobile || wasCompactMobile !== this.isCompactMobile) {
        this.rebuildAndInit();
      }
    }, 150);
  }

  /**
   * Rebuild the swiper wrapper HTML and reinitialize
   * This is needed when switching between mobile/desktop breakpoints
   */
  rebuildAndInit() {
    // Destroy existing Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Restore original HTML
    if (this.originalSwiperHTML && this.swiperWrapper) {
      this.swiperWrapper.innerHTML = this.originalSwiperHTML;
    }

    // Reinitialize
    this.initSwiper();
  }

  initSwiper() {
    // Destroy existing instance if any
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Remove slides that should be hidden at current breakpoint
    this.filterSlidesForBreakpoint();

    // Count remaining visible slides
    const visibleSlides = this.swiperWrapper.querySelectorAll('.swiper-slide');
    const visibleCount = visibleSlides.length;

    if (visibleCount === 0) return;

    // Build Swiper configuration - NO LOOP (matches Hydrogen)
    const swiperConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      speed: 500,
      // Match variable slide heights on small viewports (headless: natural image height, not fixed box)
      autoHeight: this.isCompactMobile,
      allowTouchMove: visibleCount > 1,

      // No loop - matches Hydrogen Carousel behavior
      loop: false,

      // Autoplay configuration
      autoplay: this.autoplay && visibleCount > 1 ? {
        delay: this.autoplaySpeed * 1000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      } : false,

      // Navigation arrows
      navigation: this.showArrows && visibleCount > 1 ? {
        prevEl: this.prevButton,
        nextEl: this.nextButton,
      } : false,

      // Pagination dots
      pagination: this.showDots && visibleCount > 1 ? {
        el: this.paginationEl,
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      } : false,

      // Accessibility
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
        firstSlideMessage: 'This is the first slide',
        lastSlideMessage: 'This is the last slide',
      },

      // Observer for dynamic content
      observer: true,
      observeParents: true,

      // Events
      on: {
        slideChange: this.handleSlideChange,
      },
    };

    // Initialize Swiper
    this.swiper = new Swiper(this.swiperContainer, swiperConfig);

    // Setup video handling
    this.setupVideoHandlers();

    // Log in design mode for debugging
    if (window.Shopify && window.Shopify.designMode) {
      console.log('[JKL Slideshow] Initialized', {
        slidesCount: this.slidesCount,
        visibleCount: visibleCount,
        isMobile: this.isMobile,
        autoplay: this.autoplay,
      });
    }
  }

  /**
   * Remove slides that should be hidden at the current breakpoint
   * Desktop: Remove mobile-only slides
   * Mobile: Remove desktop-only slides
   */
  filterSlidesForBreakpoint() {
    const slides = this.swiperWrapper.querySelectorAll('.swiper-slide');

    slides.forEach(slide => {
      const isDesktopOnly = slide.classList.contains('jkl-slide-wrapper--desktop-only');
      const isMobileOnly = slide.classList.contains('jkl-slide-wrapper--mobile-only');

      // Remove slides that shouldn't be visible at current breakpoint
      if (this.isMobile && isDesktopOnly) {
        slide.remove();
      } else if (!this.isMobile && isMobileOnly) {
        slide.remove();
      }
    });
  }

  /**
   * Handle slide change event
   * Pause/play videos as needed
   */
  handleSlideChange() {
    if (!this.swiper) return;

    // Pause all videos
    this.querySelectorAll('.jkl-slide__video').forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Start the video in the active slide
    const activeSlide = this.swiper.slides[this.swiper.activeIndex];
    this.playVideoInSlide(activeSlide);
  }

  /**
   * Setup video event handlers
   * Auto-advance when video ends (if not looping)
   */
  setupVideoHandlers() {
    const videos = this.querySelectorAll('.jkl-slide__video');

    videos.forEach(video => {
      // If the video is looping, the slider should NOT auto-advance
      // Swiper autoplay remains active (not stopped by the video)
      if (video.loop) return; // <-- skip all logic for looping videos

      // When the video ends (only if NOT looping), advance to the next slide
      video.addEventListener('ended', () => {
        if (this.swiper) {
          this.swiper.slideNext();
        }
      });

      // Stop Swiper autoplay while the video is playing
      video.addEventListener('play', () => {
        if (this.swiper?.autoplay) {
          this.swiper.autoplay.stop();
        }
      });

      // Resume autoplay when the video ends or is paused
      video.addEventListener('pause', () => {
        if (this.swiper?.autoplay && this.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    });

    //Start the video in the first slide
    this.playVideoInSlide(this.swiper?.slides?.[0]);
  }

  /**
   * Handle Theme Editor section load event
   */
  handleSectionLoad(event) {
    if (event.detail && event.detail.sectionId === this.sectionId) {
      // Re-initialize after a short delay to ensure DOM is updated
      setTimeout(() => {
        // Reset original HTML to capture any editor changes
        this.originalSwiperHTML = null;
        this.init();
      }, 100);
    }
  }

  /**
   * Play video in a specific slide
   */
  playVideoInSlide(slide) {
    if (!slide) return;
    const videos = slide.querySelectorAll('.jkl-slide__video');
    videos.forEach(video => {
      video.muted = true;
      video.play().catch(err => {
        console.warn('[JKL Slideshow] Autoplay blocked:', err);
      });
    });
  }

  /**
   * Cleanup when element is removed
   */
  disconnectedCallback() {
    // Clear resize timer
    clearTimeout(this.resizeTimer);

    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    // Remove event listeners
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    window.removeEventListener('resize', this.handleResize);
  }
}

// Register custom element
if (!customElements.get('jkl-slideshow')) {
  customElements.define('jkl-slideshow', JklSlideshow);
}
